import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useFollowing } from '@/hooks/useFollowing';
import type { NostrEvent } from '@nostrify/nostrify';
import type { MusicTrack } from '@/hooks/useMusicTracks';

function parseMusicTrack(event: NostrEvent): MusicTrack | null {
  const title = event.tags.find(([name]) => name === 'title')?.[1];
  const artist = event.tags.find(([name]) => name === 'artist')?.[1];
  const url = event.tags.find(([name]) => name === 'url')?.[1];
  const hash = event.tags.find(([name]) => name === 'x')?.[1];
  const mimeType = event.tags.find(([name]) => name === 'm')?.[1];

  if (!title || !url || !hash) {
    return null;
  }

  const durationStr = event.tags.find(([name]) => name === 'duration')?.[1];
  const duration = durationStr ? parseInt(durationStr) : 0;

  const album = event.tags.find(([name]) => name === 'album')?.[1];
  const genres = event.tags.filter(([name]) => name === 't').map(([_, genre]) => genre);
  const coverImage = event.tags.find(([name]) => name === 'image')?.[1];
  const publishedAtStr = event.tags.find(([name]) => name === 'published_at')?.[1];
  const fileSizeStr = event.tags.find(([name]) => name === 'size')?.[1];

  return {
    event,
    id: event.id,
    title,
    artist: artist || 'Unknown Artist',
    url,
    hash,
    mimeType: mimeType || 'audio/mpeg',
    duration: isNaN(duration) ? 0 : duration,
    album,
    genres,
    coverImage,
    publishedAt: publishedAtStr ? parseInt(publishedAtStr) : event.created_at,
    fileSize: fileSizeStr ? parseInt(fileSizeStr) : undefined,
    description: event.content,
  };
}

/**
 * Hook to fetch music tracks from artists the current user follows
 */
export function useFollowingTracks(limit: number = 50) {
  const { nostr } = useNostr();
  const { data: following, isLoading: followingLoading } = useFollowing();

  return useQuery({
    queryKey: ['following-tracks', following, limit],
    queryFn: async () => {
      if (!following || following.length === 0) return [];

      // Query tracks from followed artists
      const events = await nostr.query([{
        kinds: [30999],
        authors: following,
        limit,
      }]);

      const tracks = events
        .map(parseMusicTrack)
        .filter((track): track is MusicTrack => track !== null)
        .sort((a, b) => b.publishedAt - a.publishedAt);

      return tracks;
    },
    enabled: !followingLoading && !!following && following.length > 0,
    staleTime: 1000 * 30,
  });
}
